/**
 * Pending Transactions List Component
 * Shows list of transactions waiting to be synced
 */
import { useConfirm } from '../contexts/ConfirmContext';

export default function PendingTransactionsList({
  transactions,
  onRetry,
  onSkip
}) {
  const confirm = useConfirm();

  if (!transactions || transactions.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        color: '#6b7280'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <p style={{ margin: 0 }}>ไม่มีรายการรอซิงค์</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#b45309', text: 'รอซิงค์' },
      syncing: { bg: '#dbeafe', color: '#2563eb', text: 'กำลังซิงค์' },
      synced: { bg: '#dcfce7', color: '#166534', text: 'ซิงค์แล้ว' },
      conflict: { bg: '#fee2e2', color: '#dc2626', text: 'ต้องตรวจสอบ' },
      failed: { bg: '#fee2e2', color: '#dc2626', text: 'ล้มเหลว' },
      needs_review: { bg: '#fef3c7', color: '#b45309', text: 'รอตรวจสอบ' },
      resolved: { bg: '#e5e7eb', color: '#6b7280', text: 'แก้ไขแล้ว' }
    };

    const style = styles[status] || styles.pending;

    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {style.text}
      </span>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return formatTime(timestamp);
    }

    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {transactions.map((txn) => (
        <div
          key={txn.offlineId}
          style={{
            background: 'white',
            // body ตั้งสีตัวอักษรเริ่มต้นเป็น #e0e0e0 (ธีมมืด) — กล่องพื้นสว่างต้องกำหนดสีเอง
            color: '#2d3748',
            border: txn.syncStatus === 'conflict' || txn.syncStatus === 'failed'
              ? '2px solid #fecaca'
              : '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '16px'
              }}>
                โต๊ะ {txn.tableCode}
              </span>
              {getStatusBadge(txn.syncStatus)}
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937'
            }}>
              {(txn.grandTotal || 0).toFixed(2)} ฿
            </div>
          </div>

          {/* Details */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            fontSize: '13px',
            color: '#6b7280',
            marginBottom: '12px'
          }}>
            <div>📅 {formatDate(txn.createdAt)}</div>
            <div>🍽️ บุฟเฟ่ต์: {(txn.buffetTotal || 0).toFixed(2)}฿</div>
            <div>🔄 พยายาม: {txn.syncAttempts || 0} ครั้ง</div>
            <div>🍜 เมนูเพิ่ม: {(txn.menuTotal || 0).toFixed(2)}฿</div>
          </div>

          {/* Error message */}
          {txn.errorMessage && (
            <div style={{
              background: '#fef2f2',
              color: '#dc2626',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '13px',
              marginBottom: '12px'
            }}>
              ⚠️ {txn.errorMessage}
            </div>
          )}

          {/* Actions */}
          {(txn.syncStatus === 'failed' || txn.syncStatus === 'conflict' || txn.syncStatus === 'needs_review') && (
            <div style={{ display: 'flex', gap: '10px' }}>
              {onRetry && txn.syncStatus !== 'conflict' && (
                <button
                  onClick={() => onRetry(txn.offlineId)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  🔄 ลองใหม่
                </button>
              )}
              {onSkip && (
                <button
                  onClick={async () => {
                    if (await confirm('ต้องการข้ามรายการนี้หรือไม่? (จะไม่ซิงค์ไปยัง server)', { title: 'ข้ามรายการนี้', cancelText: 'ไม่ข้าม' })) {
                      onSkip(txn.offlineId, 'Manually skipped by admin');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#e5e7eb',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ⏭️ ข้าม
                </button>
              )}
            </div>
          )}

          {/* Syncing indicator */}
          {txn.syncStatus === 'syncing' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#2563eb',
              fontSize: '13px'
            }}>
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #2563eb',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              กำลังซิงค์...
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
